import { z } from "zod/v3";
import { sanitizeCardMeta } from "./agentChat/cardDisplayUtils";
import { normalizeChatDisplayText } from "./normalizeChatDisplayText";

const linkItemSchema = z.object({
  label: z.string(),
  href: z.string(),
  description: z.string().optional().nullable()
});

const personBlockSchema = z.object({
  type: z.literal("person"),
  pgaId: z.string(),
  name: z.string(),
  subtitle: z.string().optional(),
  imageUrl: z.string().optional(),
  links: z.array(linkItemSchema).optional()
});

const interactionBlockSchema = z.object({
  type: z.literal("interaction"),
  pgaId: z.string(),
  interactionId: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  meta: z.string().optional()
});

const linksBlockSchema = z.object({
  type: z.literal("links"),
  title: z.string().optional(),
  links: z.array(linkItemSchema)
});

const recordListItemSchema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  meta: z.string().optional(),
  href: z.string().optional(),
  imageUrl: z.string().optional(),
  kind: z.enum(["person", "event", "facility", "section", "job", "generic"]).optional(),
  facilityName: z.string().optional(),
  facilityHref: z.string().optional(),
  location: z.string().optional(),
  links: z.array(linkItemSchema).optional()
});

const recordListBlockSchema = z.object({
  type: z.literal("record_list"),
  title: z.string().optional(),
  items: z.array(recordListItemSchema)
});

export const m360StructuredBlockSchema = z.discriminatedUnion("type", [
  personBlockSchema,
  interactionBlockSchema,
  linksBlockSchema,
  recordListBlockSchema
]);

export type M360StructuredBlock = z.infer<typeof m360StructuredBlockSchema>;

export type ChatContentSegment =
  | { kind: "markdown"; value: string }
  | { kind: "structured"; value: M360StructuredBlock }
  | { kind: "json"; value: unknown };

const FENCE_PATTERN = /```(m360|json)\s*\n([\s\S]*?)```/g;
const SEARCH_HEADER_PATTERN = /^Found (\d+) matching (person|people)(?: \(showing (\d+)\))?:?\s*/i;
const EVENT_SEARCH_HEADER_PATTERN = /^Found (\d+) (events?|seminars?)(?: \(showing (\d+)\))?:?\s*/im;
const COMMITTEE_SEARCH_HEADER_PATTERN = /^Found (\d+) committees?(?: \(showing (\d+)\))?:?\s*/im;
const SECTION_SEARCH_HEADER_PATTERN = /^Found (\d+) sections?(?: \(showing (\d+)\))?:?\s*/im;
const FACILITY_SEARCH_HEADER_PATTERN = /^Found (\d+) matching facilit(?:y|ies)(?: \(showing (\d+)\))?:?\s*/im;
const JOB_POST_SEARCH_HEADER_PATTERN = /^Found (\d+) job posts?(?: \(showing (\d+)\))?:?\s*/im;
const TASK_SEARCH_HEADER_PATTERN = /^Found (\d+) tasks?(?: \(showing (\d+)\))?:?\s*/im;
const SEARCH_RESULT_BLOCK_SPLIT =
  /(?=\n*Found \d+ (?:matching (?:person|people)|events?|seminars?|committees?|sections?|matching facilit(?:y|ies)|job posts?|tasks?)(?: \(showing \d+\))?:?\s*)/im;
const COMMITTEE_HISTORY_HEADER_PATTERN = /^Committee history for .+ \(PGA ID [^)]+\):\s*/im;
const PROFILE_SUMMARY_PATTERN = /(?:Here is the )?profile summary for\s+(.+?)\s*\(PGA ID\s+([^)]+)\)\s*:?/i;

type RecordListItem = z.infer<typeof recordListItemSchema>;

export function pgaIdFromPersonHref(href?: string): string | null {
  if (!href) {
    return null;
  }

  const match = href.match(/^\/people\/([^/]+)\//);
  return match?.[1] ?? null;
}

function recordListItemToPersonCard(item: RecordListItem): Extract<M360StructuredBlock, { type: "person" }> | null {
  const pgaId = pgaIdFromPersonHref(item.href);
  if (!pgaId) {
    return null;
  }

  return {
    type: "person",
    pgaId,
    name: item.title,
    subtitle: item.subtitle,
    imageUrl: item.imageUrl
  };
}

function upgradeStructuredBlock(block: M360StructuredBlock): M360StructuredBlock {
  if (block.type === "record_list" && block.items.length === 1) {
    const personCard = recordListItemToPersonCard(block.items[0]);
    if (personCard) {
      return personCard;
    }
  }

  return block;
}

function normalizeStructuredBlock(block: M360StructuredBlock): M360StructuredBlock {
  switch (block.type) {
    case "person":
      return {
        ...block,
        name: normalizeChatDisplayText(block.name),
        subtitle: block.subtitle ? normalizeChatDisplayText(block.subtitle) : undefined
      };
    case "interaction":
      return {
        ...block,
        title: normalizeChatDisplayText(block.title),
        subtitle: block.subtitle ? normalizeChatDisplayText(block.subtitle) : undefined,
        meta: block.meta ? normalizeChatDisplayText(block.meta) : undefined
      };
    case "record_list":
      return {
        ...block,
        title: block.title ? normalizeChatDisplayText(block.title) : undefined,
        items: block.items.map((item) => ({
          ...item,
          title: normalizeChatDisplayText(item.title),
          subtitle: item.subtitle ? normalizeChatDisplayText(item.subtitle) : undefined,
          meta: item.meta ? sanitizeCardMeta(normalizeChatDisplayText(item.meta)) : undefined,
          links: item.links?.map((link) => ({
            ...link,
            label: normalizeChatDisplayText(link.label)
          }))
        }))
      };
    case "links":
      return {
        ...block,
        title: block.title ? normalizeChatDisplayText(block.title) : undefined,
        links: block.links.map((link) => ({
          ...link,
          label: normalizeChatDisplayText(link.label)
        }))
      };
    default:
      return block;
  }
}

function finalizeStructuredBlock(block: M360StructuredBlock): M360StructuredBlock {
  return normalizeStructuredBlock(upgradeStructuredBlock(block));
}

/** Same single-item list → person upgrade used by legacy and json-render paths. */
export function upgradeAgentStructuredBlock(block: M360StructuredBlock): M360StructuredBlock {
  return finalizeStructuredBlock(block);
}

function normalizeDuplicateKey(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** Collapse repeated search-result blocks the agent may echo multiple times. */
export function collapseRedundantAssistantText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return trimmed;
  }

  const parts = trimmed.split(SEARCH_RESULT_BLOCK_SPLIT);
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const part of parts) {
    const normalized = normalizeDuplicateKey(part);
    if (!normalized) {
      continue;
    }
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    unique.push(part.trim());
  }

  return unique.join("\n\n");
}

function parseSearchResultLine(line: string): RecordListItem | null {
  const linked = line.match(/^-\s+\[([^\]]+)\]\((\/people\/[^)]+)\)(?:\s*\(PGA ID ([^)]+)\))?(.*)$/);
  if (linked) {
    const [, name, href, , rest] = linked;
    const subtitle = rest?.replace(/^[\s:—-]+/, "").trim();
    return { title: name, href, subtitle: subtitle || undefined };
  }

  const inline = line.match(/^-\s+(.+?)\s*\(PGA ID ([^)]+)\)(.*)$/);
  if (inline) {
    const [, name, pgaId, rest] = inline;
    const subtitle = rest?.replace(/^[\s:—-]+/, "").trim();
    return {
      title: name.trim(),
      href: `/people/${pgaId}/person/overview`,
      subtitle: subtitle || undefined
    };
  }

  return null;
}

function parseEventSearchTitleLine(line: string): Pick<RecordListItem, "title" | "href"> | null {
  const linked = line.match(/^-\s+\[([^\]]+)\]\((\/events\/[^)]+)\)/);
  if (linked) {
    return { title: linked[1].trim(), href: linked[2] };
  }

  const plain = line.match(/^-\s+(.+)$/);
  if (!plain) {
    return null;
  }

  const title = plain[1].replace(/\s*\(slug\s+[\w-]+\)\s*$/i, "").trim();
  return { title };
}

function parseEventSearchDetails(
  details: string[]
): Pick<RecordListItem, "subtitle" | "meta" | "href" | "links" | "facilityName" | "facilityHref" | "location"> {
  let href: string | undefined;
  let facilityName: string | undefined;
  let facilityHref: string | undefined;
  let location: string | undefined;
  const metaParts: string[] = [];
  const links: RecordListItem["links"] = [];

  for (const rawLine of details) {
    const line = rawLine.trim();
    const open = line.match(/^Open:\s*(\/events\/[^\s]+)/i);
    if (open) {
      href = open[1];
      continue;
    }

    const facilityLink = line.match(/^Facility:\s*\[([^\]]+)\]\((\/facilities\/[^)]+)\)\s*$/i);
    if (facilityLink) {
      facilityName = facilityLink[1].trim();
      facilityHref = facilityLink[2];
      continue;
    }

    const facilityPlain = line.match(/^Facility:\s*(.+)$/i);
    if (facilityPlain) {
      const value = facilityPlain[1].trim();
      if (value && value !== "N/A") {
        facilityName = value;
      }
      continue;
    }

    const locationLine = line.match(/^Location:\s*(.+)$/i);
    if (locationLine) {
      const value = locationLine[1].trim();
      if (value && value !== "Location TBD" && value !== "N/A") {
        location = value;
      }
      continue;
    }

    const when = line.match(/^When:\s*(.+)$/i);
    if (when) {
      const value = when[1].trim();
      if (value && value !== "N/A") {
        metaParts.push(value);
      }
      continue;
    }

    const statusType = line.match(/^Status:\s*(.+)$/i);
    if (statusType) {
      const value = sanitizeCardMeta(statusType[1]) ?? statusType[1].trim();
      if (value) {
        metaParts.push(value);
      }
    }

    const sectionLink = line.match(/\[([^\]]+)\]\((\/sections\/[^)]+)\)/);
    if (sectionLink) {
      links?.push({ label: sectionLink[1], href: sectionLink[2] });
    }
  }

  return {
    href,
    facilityName,
    facilityHref,
    location,
    meta: metaParts.length > 0 ? metaParts.join(" · ") : undefined,
    links: links && links.length > 0 ? links : undefined
  };
}

function parseEventSearchResultItems(body: string): RecordListItem[] {
  const items: RecordListItem[] = [];
  let currentTitle: Pick<RecordListItem, "title" | "href"> | null = null;
  let currentDetails: string[] = [];

  const flush = () => {
    if (!currentTitle) {
      return;
    }

    const details = parseEventSearchDetails(currentDetails);
    items.push({
      title: currentTitle.title,
      href: currentTitle.href ?? details.href,
      facilityName: details.facilityName,
      facilityHref: details.facilityHref,
      location: details.location,
      meta: details.meta,
      links: details.links,
      kind: "event"
    });
    currentTitle = null;
    currentDetails = [];
  };

  for (const rawLine of body.split("\n")) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      continue;
    }

    if (/^-\s+/.test(line)) {
      flush();
      currentTitle = parseEventSearchTitleLine(line);
      continue;
    }

    if (currentTitle && /^\s{2,}\S/.test(rawLine)) {
      currentDetails.push(line.trim());
    }
  }

  flush();
  return items;
}

function tryParseEventSearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  const headerMatch = markdown.match(EVENT_SEARCH_HEADER_PATTERN);
  if (!headerMatch) {
    return null;
  }

  const body = markdown.slice(headerMatch.index! + headerMatch[0].length);
  const items = parseEventSearchResultItems(body);
  if (items.length === 0) {
    return null;
  }

  const showing = headerMatch[3];
  const noun = headerMatch[2].toLowerCase().startsWith("seminar") ? "seminars" : "events";
  const title = showing ? `Upcoming ${noun} (showing ${showing})` : `Upcoming ${noun}`;

  return {
    type: "record_list",
    title,
    items
  };
}

const EVENT_MARKDOWN_LINK_PATTERN = /^\s*[-*]?\s*\[([^\]]+)\]\((\/events\/[^)]+)\)(.*)$/;
const INLINE_EVENT_LINK_PATTERN = /\[([^\]]+)\]\((\/events\/[^)]+)\)/g;

function tryParseInlineEventMarkdownLinks(markdown: string): M360StructuredBlock | null {
  const items: RecordListItem[] = [];
  const seenHrefs = new Set<string>();

  for (const match of markdown.matchAll(INLINE_EVENT_LINK_PATTERN)) {
    const title = match[1]?.trim();
    const href = match[2];
    if (!title || !href || seenHrefs.has(href)) {
      continue;
    }

    seenHrefs.add(href);
    items.push({
      title,
      href,
      kind: "event"
    });
  }

  if (items.length === 0) {
    return null;
  }

  return {
    type: "record_list",
    title: "Upcoming events",
    items
  };
}

function tryParseEventMarkdownLinks(markdown: string): M360StructuredBlock | null {
  const items: RecordListItem[] = [];

  for (const rawLine of markdown.split("\n")) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }

    const match = line.match(EVENT_MARKDOWN_LINK_PATTERN);
    if (!match) {
      continue;
    }

    const [, title, href, rest] = match;
    const subtitle = rest?.replace(/^[\s:—-]+/, "").trim();

    items.push({
      title: title.trim(),
      href,
      subtitle: subtitle || undefined,
      kind: "event"
    });
  }

  if (items.length === 0) {
    return null;
  }

  return {
    type: "record_list",
    title: "Upcoming events",
    items
  };
}

function tryParseEventMarkdown(markdown: string): M360StructuredBlock | null {
  return (
    tryParseEventSearchResultsMarkdown(markdown) ??
    tryParseEventMarkdownLinks(markdown) ??
    tryParseInlineEventMarkdownLinks(markdown)
  );
}

function parseIndentedSearchItems(
  body: string,
  parseTitleLine: (line: string) => RecordListItem | null,
  enrichFromDetails?: (item: RecordListItem, details: string[]) => RecordListItem
): RecordListItem[] {
  const items: RecordListItem[] = [];
  let current: RecordListItem | null = null;
  let currentDetails: string[] = [];

  const flush = () => {
    if (!current) {
      return;
    }

    items.push(enrichFromDetails ? enrichFromDetails(current, currentDetails) : current);
    current = null;
    currentDetails = [];
  };

  for (const rawLine of body.split("\n")) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      continue;
    }

    if (/^-\s+/.test(line)) {
      flush();
      current = parseTitleLine(line.trim());
      continue;
    }

    if (current && /^\s{2,}\S/.test(rawLine)) {
      currentDetails.push(line.trim());
    }
  }

  flush();
  return items;
}

function tryParseSearchResultsBlock(
  markdown: string,
  headerPattern: RegExp,
  listTitle: (showing?: string) => string,
  parseItems: (body: string) => RecordListItem[]
): M360StructuredBlock | null {
  const headerMatch = markdown.match(headerPattern);
  if (!headerMatch) {
    return null;
  }

  const body = markdown.slice(headerMatch.index! + headerMatch[0].length);
  const items = parseItems(body);
  if (items.length === 0) {
    return null;
  }

  return {
    type: "record_list",
    title: listTitle(headerMatch[2]),
    items
  };
}

function parseCommitteeSearchTitleLine(line: string): RecordListItem | null {
  const match = line.match(/^-\s+(.+?)\s*\(ID ([^)]+)\)\s*$/);
  if (!match) {
    return null;
  }

  return {
    title: match[1].trim(),
    kind: "generic"
  };
}

function enrichCommitteeSearchItem(item: RecordListItem, details: string[]): RecordListItem {
  const metaParts: string[] = [];
  let subtitle: string | undefined;

  for (const detail of details) {
    const section = detail.match(/^Section:\s*(.+)$/i);
    if (section) {
      const value = section[1].trim();
      if (value && value !== "N/A") {
        subtitle = value;
      }
      continue;
    }

    const status = detail.match(/^Status:\s*(.+)$/i);
    if (status) {
      const value = sanitizeCardMeta(status[1]) ?? status[1].trim();
      if (value) {
        metaParts.push(value);
      }
      continue;
    }

    const participants = detail.match(/^Participants:\s*(.+)$/i);
    if (participants) {
      metaParts.push(participants[1].trim());
    }
  }

  return {
    ...item,
    subtitle,
    meta: metaParts.length > 0 ? metaParts.join(" · ") : undefined
  };
}

function tryParseCommitteeSearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  return tryParseSearchResultsBlock(
    markdown,
    COMMITTEE_SEARCH_HEADER_PATTERN,
    (showing) => (showing ? `Committees (showing ${showing})` : "Committees"),
    (body) =>
      parseIndentedSearchItems(body, parseCommitteeSearchTitleLine, enrichCommitteeSearchItem).map((item) => ({
        ...item,
        kind: "generic" as const
      }))
  );
}

function parseSectionSearchTitleLine(line: string): RecordListItem | null {
  const match = line.match(/^-\s+(.+?)\s*\(ID ([^,]+)(?:,\s*legacy ([^)]+))?\)\s*$/);
  if (!match) {
    return null;
  }

  const title = match[1].trim();
  const sectionId = match[2].trim();
  const legacyId = match[3]?.trim();

  return {
    title,
    href: `/sections/${sectionId}`,
    subtitle: legacyId && legacyId !== "N/A" ? `Legacy ${legacyId}` : undefined,
    kind: "section"
  };
}

function tryParseSectionSearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  return tryParseSearchResultsBlock(
    markdown,
    SECTION_SEARCH_HEADER_PATTERN,
    (showing) => (showing ? `Sections (showing ${showing})` : "Sections"),
    (body) => parseIndentedSearchItems(body, parseSectionSearchTitleLine)
  );
}

function parseFacilitySearchResultLine(line: string): RecordListItem | null {
  const linked = line.match(/^-\s+\[([^\]]+)\]\((\/facilities\/[^)]+)\)(?:\s*\(ID ([^)]+)\))?(.*)$/);
  if (!linked) {
    return null;
  }

  const [, name, href, , rest] = linked;
  const subtitle = rest?.replace(/^[\s:—-]+/, "").trim();

  return {
    title: name,
    href,
    subtitle: subtitle || undefined,
    kind: "facility"
  };
}

function enrichFacilitySearchItem(item: RecordListItem, details: string[]): RecordListItem {
  const metaParts: string[] = [];

  for (const detail of details) {
    const type = detail.match(/^Type:\s*(.+)$/i);
    if (type) {
      const value = type[1].trim();
      if (value && value !== "N/A") {
        metaParts.push(value);
      }
      continue;
    }

    const section = detail.match(/^Section:\s*(.+)$/i);
    if (section) {
      const value = section[1].trim();
      if (value && value !== "N/A") {
        metaParts.push(value);
      }
      continue;
    }

    const address = detail.match(/^Address:\s*(.+)$/i);
    if (address) {
      const value = address[1].trim();
      if (value && value !== "N/A") {
        return {
          ...item,
          subtitle: item.subtitle ?? value,
          meta: metaParts.length > 0 ? metaParts.join(" · ") : item.meta
        };
      }
    }
  }

  return {
    ...item,
    meta: metaParts.length > 0 ? metaParts.join(" · ") : item.meta
  };
}

function tryParseFacilitySearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  return tryParseSearchResultsBlock(
    markdown,
    FACILITY_SEARCH_HEADER_PATTERN,
    (showing) => (showing ? `Matching facilities (showing ${showing})` : "Matching facilities"),
    (body) => parseIndentedSearchItems(body, parseFacilitySearchResultLine, enrichFacilitySearchItem)
  );
}

function parseJobPostSearchTitleLine(line: string): RecordListItem | null {
  const match = line.match(/^-\s+(.+)$/);
  if (!match) {
    return null;
  }

  return {
    title: match[1].trim(),
    kind: "job"
  };
}

function enrichJobPostSearchItem(item: RecordListItem, details: string[]): RecordListItem {
  let href = item.href;
  let facilityName: string | undefined;
  let facilityHref: string | undefined;
  const metaParts: string[] = [];

  for (const detail of details) {
    const open = detail.match(/^Open:\s*(\/jobs\/job-post\/[^\s]+)/i);
    if (open) {
      href = open[1];
      continue;
    }

    const facilityLink = detail.match(/^Facility:\s*\[([^\]]+)\]\((\/facilities\/[^)]+)\)\s*$/i);
    if (facilityLink) {
      facilityName = facilityLink[1].trim();
      facilityHref = facilityLink[2];
      continue;
    }

    const facilityPlain = detail.match(/^Facility:\s*(.+)$/i);
    if (facilityPlain) {
      const value = facilityPlain[1].trim();
      if (value && value !== "N/A") {
        facilityName = value;
      }
      continue;
    }

    const status = detail.match(/^Status:\s*(.+)$/i);
    if (status) {
      const value = sanitizeCardMeta(status[1]) ?? status[1].trim();
      if (value) {
        metaParts.push(value);
      }
    }
  }

  return {
    ...item,
    href,
    facilityName,
    facilityHref,
    meta: metaParts.length > 0 ? metaParts.join(" · ") : item.meta,
    kind: "job"
  };
}

function tryParseJobPostSearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  return tryParseSearchResultsBlock(
    markdown,
    JOB_POST_SEARCH_HEADER_PATTERN,
    (showing) => (showing ? `Job posts (showing ${showing})` : "Job posts"),
    (body) => parseIndentedSearchItems(body, parseJobPostSearchTitleLine, enrichJobPostSearchItem)
  );
}

function parseTaskSearchTitleLine(line: string): RecordListItem | null {
  const match = line.match(/^-\s+(.+?)\s*\|\s*(.+?)\s*\|\s*due\s+(.+)$/i);
  if (!match) {
    return null;
  }

  return {
    title: match[1].trim(),
    meta: `${match[2].trim()} · Due ${match[3].trim()}`,
    kind: "generic"
  };
}

function enrichTaskSearchItem(item: RecordListItem, details: string[]): RecordListItem {
  const metaParts = item.meta ? [item.meta] : [];

  for (const detail of details) {
    const assignee = detail.match(/^Assignee:\s*(.+)$/i);
    if (assignee) {
      metaParts.push(`Assignee: ${assignee[1].trim()}`);
    }
  }

  return {
    ...item,
    meta: metaParts.length > 0 ? metaParts.join(" · ") : undefined
  };
}

function tryParseTaskSearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  return tryParseSearchResultsBlock(
    markdown,
    TASK_SEARCH_HEADER_PATTERN,
    (showing) => (showing ? `Tasks (showing ${showing})` : "Tasks"),
    (body) => parseIndentedSearchItems(body, parseTaskSearchTitleLine, enrichTaskSearchItem)
  );
}

function parseCommitteeHistoryTitleLine(line: string): RecordListItem | null {
  const match = line.match(/^-\s+(.+?)\s*\(([^)]+)\)\s*$/);
  if (!match) {
    return null;
  }

  return {
    title: match[1].trim(),
    subtitle: match[2].trim(),
    kind: "generic"
  };
}

function enrichCommitteeHistoryItem(item: RecordListItem, details: string[]): RecordListItem {
  const metaParts: string[] = [];

  for (const detail of details) {
    const title = detail.match(/^Title:\s*(.+)$/i);
    if (title) {
      metaParts.push(title[1].trim());
      continue;
    }

    const term = detail.match(/^Term:\s*(.+)$/i);
    if (term) {
      metaParts.push(term[1].trim());
    }
  }

  return {
    ...item,
    meta: metaParts.length > 0 ? metaParts.join(" · ") : item.meta
  };
}

function tryParseCommitteeHistoryMarkdown(markdown: string): M360StructuredBlock | null {
  const headerMatch = markdown.match(COMMITTEE_HISTORY_HEADER_PATTERN);
  if (!headerMatch) {
    return null;
  }

  const body = markdown.slice(headerMatch.index! + headerMatch[0].length);
  const items = parseIndentedSearchItems(body, parseCommitteeHistoryTitleLine, enrichCommitteeHistoryItem);
  if (items.length === 0) {
    return null;
  }

  return {
    type: "record_list",
    title: "Committee history",
    items
  };
}

/** Strip CountRecords filter metadata before showing unpromoted tool markdown. */
export function sanitizeToolResultsMarkdown(toolResults: string): string {
  return toolResults.replace(/\n\nFilters:\n[\s\S]*$/, "").trim();
}

function parseSearchResultItems(body: string): RecordListItem[] {
  const items: RecordListItem[] = [];
  let current: RecordListItem | null = null;

  for (const rawLine of body.split("\n")) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      continue;
    }

    if (/^-\s+/.test(line)) {
      if (current) {
        items.push(current);
      }
      current = parseSearchResultLine(line);
      continue;
    }

    if (current && /^\s{2,}\S/.test(rawLine)) {
      const detail = line.trim();
      current.subtitle = current.subtitle ? `${current.subtitle} · ${detail}` : detail;
    }
  }

  if (current) {
    items.push(current);
  }

  return items;
}

/** Promote agent prose (search hits, profile summaries) into a structured card when spec data is missing. */
export function promoteAgentProseToStructuredBlock(prose: string): M360StructuredBlock | null {
  const collapsed = collapseRedundantAssistantText(prose);

  const events = tryParseEventMarkdown(collapsed);
  if (events) {
    return finalizeStructuredBlock(events);
  }

  const search = tryParseLegacySearchResultsMarkdown(collapsed);
  if (search) {
    return finalizeStructuredBlock(search);
  }

  const committees = tryParseCommitteeSearchResultsMarkdown(collapsed);
  if (committees) {
    return finalizeStructuredBlock(committees);
  }

  const committeeHistory = tryParseCommitteeHistoryMarkdown(collapsed);
  if (committeeHistory) {
    return finalizeStructuredBlock(committeeHistory);
  }

  const sections = tryParseSectionSearchResultsMarkdown(collapsed);
  if (sections) {
    return finalizeStructuredBlock(sections);
  }

  const facilities = tryParseFacilitySearchResultsMarkdown(collapsed);
  if (facilities) {
    return finalizeStructuredBlock(facilities);
  }

  const jobPosts = tryParseJobPostSearchResultsMarkdown(collapsed);
  if (jobPosts) {
    return finalizeStructuredBlock(jobPosts);
  }

  const tasks = tryParseTaskSearchResultsMarkdown(collapsed);
  if (tasks) {
    return finalizeStructuredBlock(tasks);
  }

  const profileSummary = tryParseProfileSummaryMarkdown(collapsed);
  const structured = profileSummary?.find((segment): segment is Extract<ChatContentSegment, { kind: "structured" }> => {
    return segment.kind === "structured";
  });

  return structured ? structured.value : null;
}

function tryParseLegacySearchResultsMarkdown(markdown: string): M360StructuredBlock | null {
  const headerMatch = markdown.match(SEARCH_HEADER_PATTERN);
  if (!headerMatch) {
    return null;
  }

  const body = markdown.slice(headerMatch[0].length);
  const items = parseSearchResultItems(body);
  if (items.length === 0) {
    return null;
  }

  const showing = headerMatch[3];
  if (items.length === 1) {
    const personCard = recordListItemToPersonCard(items[0]);
    if (personCard) {
      return personCard;
    }
  }

  const title = showing ? `Matching people (showing ${showing})` : "Matching people";

  return {
    type: "record_list",
    title,
    items
  };
}

function extractProfileSubtitle(markdown: string): string | undefined {
  const lines = markdown.split("\n").map((line) => line.trim());
  const fields: string[] = [];

  for (const line of lines) {
    const status = line.match(/^-?\s*Status:\s*(.+)$/i);
    if (status) {
      fields.push(status[1].trim());
      continue;
    }

    const classification = line.match(/^-?\s*Classification:\s*(.+)$/i);
    if (classification) {
      fields.push(classification[1].trim());
      continue;
    }

    const section = line.match(/^-?\s*Section:\s*(.+)$/i);
    if (section) {
      fields.push(section[1].trim());
    }
  }

  return fields.length > 0 ? fields.join(" · ") : undefined;
}

function tryParseProfileSummaryMarkdown(markdown: string): ChatContentSegment[] | null {
  const match = markdown.match(PROFILE_SUMMARY_PATTERN);
  if (!match) {
    return null;
  }

  const name = match[1].trim();
  const pgaId = match[2].trim();
  const leadEnd = match.index! + match[0].length;
  const remainder = markdown.slice(leadEnd).trim();

  const segments: ChatContentSegment[] = [
    {
      kind: "structured",
      value: finalizeStructuredBlock({
        type: "person",
        pgaId,
        name,
        subtitle: extractProfileSubtitle(remainder)
      })
    }
  ];

  if (remainder) {
    segments.push({ kind: "markdown", value: remainder });
  }

  return segments;
}

function promoteMarkdownSegment(markdown: string): ChatContentSegment[] {
  const profileSummary = tryParseProfileSummaryMarkdown(markdown);
  if (profileSummary) {
    return profileSummary;
  }

  const promoted = promoteAgentProseToStructuredBlock(markdown);
  if (promoted) {
    return [{ kind: "structured", value: finalizeStructuredBlock(promoted) }];
  }

  return [{ kind: "markdown", value: markdown }];
}

function parseStructuredBlock(raw: string): M360StructuredBlock | null {
  try {
    const parsed = m360StructuredBlockSchema.safeParse(JSON.parse(raw.trim()));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function parseJsonBlock(raw: string): unknown | null {
  try {
    return JSON.parse(raw.trim());
  } catch {
    return null;
  }
}

function structuredSegmentKey(segment: Extract<ChatContentSegment, { kind: "structured" }>): string {
  const block = segment.value;
  if (block.type === "person") {
    return `person:${block.pgaId}`;
  }
  if (block.type === "interaction") {
    return `interaction:${block.pgaId}:${block.interactionId}`;
  }

  return JSON.stringify(block);
}

function dedupeChatSegments(segments: ChatContentSegment[]): ChatContentSegment[] {
  const seenStructured = new Set<string>();
  const seenMarkdown = new Set<string>();

  return segments.filter((segment) => {
    if (segment.kind === "structured") {
      const key = structuredSegmentKey(segment);
      if (seenStructured.has(key)) {
        return false;
      }
      seenStructured.add(key);
      return true;
    }

    if (segment.kind !== "markdown") {
      return true;
    }

    const key = normalizeDuplicateKey(segment.value);
    if (seenMarkdown.has(key)) {
      return false;
    }
    seenMarkdown.add(key);
    return true;
  });
}

/** Split assistant text into markdown, optional m360 JSON cards, and generic JSON blocks. */
export function parseChatContentSegments(text: string): ChatContentSegment[] {
  const collapsed = collapseRedundantAssistantText(text);
  if (!collapsed.trim()) {
    return [];
  }

  const segments: ChatContentSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  FENCE_PATTERN.lastIndex = 0;

  while ((match = FENCE_PATTERN.exec(collapsed)) !== null) {
    if (match.index > lastIndex) {
      const markdown = collapsed.slice(lastIndex, match.index).trim();
      if (markdown) {
        segments.push(...promoteMarkdownSegment(markdown));
      }
    }

    const fenceType = match[1];
    const payload = match[2] ?? "";

    if (fenceType === "m360") {
      const structured = parseStructuredBlock(payload);
      if (structured) {
        segments.push({ kind: "structured", value: finalizeStructuredBlock(structured) });
      } else {
        segments.push({ kind: "markdown", value: `\`\`\`m360\n${payload}\`\`\`` });
      }
    } else if (fenceType === "json") {
      const parsed = parseJsonBlock(payload);
      if (parsed !== null) {
        segments.push({ kind: "json", value: parsed });
      } else {
        segments.push({ kind: "markdown", value: `\`\`\`json\n${payload}\`\`\`` });
      }
    }

    lastIndex = match.index + match[0].length;
  }

  const trailing = collapsed.slice(lastIndex).trim();
  if (trailing) {
    segments.push(...promoteMarkdownSegment(trailing));
  }

  if (segments.length === 0) {
    segments.push(...promoteMarkdownSegment(collapsed));
  }

  return dedupeChatSegments(segments);
}
